import { nodeConfig } from '@scripts/mixins';
import { verticalKeyNavigationHandler } from '@scripts/helpers/key-navigation';
import deeplink, { updateHash } from '@scripts/helpers/deeplink';
import { toggleHtmlClass } from '@scripts/helpers/utils';
import * as CONSTANTS from '@scripts/helpers/constants';

export const NAMESPACE = 'disclosure';
export const DEFAULT_SELECTOR = '[data-js-disclosure]';

/**
 * @typedef {Object} DisclosureSettings
 * @property {string} [namespace='disclosure'] - Data attribute namespace.
 * @property {boolean} [collapsible=true] - Allow closing via trigger.
 * @property {boolean} [hiddenUntilFound=false] - Support search-in-page.
 * @property {boolean} [closeOnEscape=true] - Close on Escape key.
 * @property {boolean} [closeOnBlur=true] - Close on focus loss.
 * @property {boolean} [closeOnOutsideClick=true] - Close on outside click.
 * @property {boolean} [restoreFocusOnClose=true] - Return focus to trigger.
 * @property {boolean} [keyboardNavigation=false] - Enable arrow key navigation.
 * @property {boolean} [loopNavigation=false] - Loop keyboard focus.
 * @property {boolean} [deeplink=false] - Enable URL hash updates.
 * @property {string|null} [htmlStateClass=null] - Class added to <html> when open.
 * @property {string|null} [mediaQuery=null] - CSS media query. Active only while matching.
 * @property {boolean} [useInert=false] - Use inert attribute instead of hidden attribute.
 */
export const DEFAULT_SETTINGS = {
	namespace: NAMESPACE,
	collapsible: true,
	hiddenUntilFound: false,
	closeOnEscape: true,
	closeOnBlur: true,
	closeOnOutsideClick: true,
	restoreFocusOnClose: true,
	keyboardNavigation: false,
	loopNavigation: false,
	deeplink: false,
	htmlStateClass: null,
	mediaQuery: null,
	useInert: false,
};

/**
 * Generic disclosure.
 * @fires disclosure:open
 * @fires disclosure:close
 */
export class Disclosure {
	/**
	 * @param {HTMLElement} node
	 * @param {DisclosureSettings} config
	 */
	constructor(node, config) {
		this.elNode = node;
		this.config = config;
		this.namespace = this.config.namespace || NAMESPACE;

		this.elButton = this.elNode.querySelector(`[data-js-${this.namespace}-button]`);
		this.elPanel = this.elNode.querySelector(`[data-js-${this.namespace}-panel]`);

		this._enabled = false;
		this._mediaQueryList = null;

		this.toggleAttribute = this.config.useInert ? 'inert' : 'hidden';
		if (this.config.useInert) {
			this.config.hiddenUntilFound = false;
		}
	}

	init() {
		if (!this.elButton || !this.elPanel) return;

		if (this.config.mediaQuery) {
			this._mediaQueryList = window.matchMedia(this.config.mediaQuery);
			this._onMediaChange = this._onMediaChange.bind(this);
			this._mediaQueryList.addEventListener('change', this._onMediaChange);

			if (this._mediaQueryList.matches) {
				this._enable();
			}
		} else {
			this._enable();
		}
	}

	destroy() {
		this._disable();

		if (this._mediaQueryList) {
			this._mediaQueryList.removeEventListener('change', this._onMediaChange);
			this._mediaQueryList = null;
		}
	}

	/** @returns {boolean} */
	isOpen() {
		if (!this._enabled) return false;

		return this.elButton.getAttribute('aria-expanded') === 'true';
	}

	toggle() {
		if (!this._enabled) return;

		if (!this.isOpen()) {
			return this.open();
		}

		if (this.config.collapsible) {
			this.close();
		}
	}

	/** @param {boolean} [updateHashState=true] */
	open(updateHashState = true) {
		if (!this._enabled || this.isOpen()) return;

		this._updateDOM(true);
		this._bindGlobalListeners();

		toggleHtmlClass(this.config.htmlStateClass, true);

		if (this.config.deeplink && updateHashState) {
			updateHash(this.elPanel.id, true);
		}

		this._dispatch('open');
	}

	/** @param {boolean} [returnFocus=true] */
	close(returnFocus = true) {
		if (!this._enabled || !this.isOpen()) return;

		this._updateDOM(false);
		this._unbindGlobalListeners();

		toggleHtmlClass(this.config.htmlStateClass, false);

		if (returnFocus && this.config.restoreFocusOnClose) {
			this.elButton.focus();
		}

		if (this.config.deeplink) updateHash(this.elPanel.id, false);

		this._dispatch('close');
	}

	_enable() {
		if (this._enabled) return;
		this._enabled = true;

		this._bindUI();
		this._setup();

		if (this.config.deeplink) {
			this._setupDeepLinks();
		}
	}

	_disable() {
		if (!this._enabled) return;

		if (this.isOpen()) {
			this._unbindGlobalListeners();
			toggleHtmlClass(this.config.htmlStateClass, false);
		}

		this.elButton.removeAttribute('aria-expanded');
		this.elButton.classList.remove(CONSTANTS.ACTIVE_CLASS);
		this.elPanel.classList.remove(CONSTANTS.OPEN_CLASS);
		this.elPanel.removeAttribute(this.toggleAttribute);

		this._unbindUI();
		this._enabled = false;
	}

	_setup() {
		if (!this.elButton.hasAttribute('aria-expanded')) {
			this.elButton.setAttribute('aria-expanded', 'false');
		}

		const startOpen = this.elButton.classList.contains(CONSTANTS.ACTIVE_CLASS);

		this._updateDOM(startOpen);

		if (startOpen) {
			this._bindGlobalListeners();
		}
	}

	_setupDeepLinks() {
		if (!this.elPanel?.id) return;

		deeplink.register(`[data-js-${this.namespace}-panel]#${this.elPanel.id}`, () => {
			this.open();
			this.elButton.focus();
		});
	}

	/** @param {boolean} isOpen */
	_updateDOM(isOpen) {
		this.elButton.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

		if (isOpen) {
			this.elButton.classList.add(CONSTANTS.ACTIVE_CLASS);
			this.elPanel.classList.add(CONSTANTS.OPEN_CLASS);
			this.elPanel.removeAttribute(this.toggleAttribute);
		} else {
			this.elButton.classList.remove(CONSTANTS.ACTIVE_CLASS);
			this.elPanel.classList.remove(CONSTANTS.OPEN_CLASS);
			this.elPanel.setAttribute(this.toggleAttribute, this.config.hiddenUntilFound ? 'until-found' : '');
		}
	}

	/** @param {string} action */
	_dispatch(action) {
		this.elNode.dispatchEvent(
			new CustomEvent(`${this.namespace}:${action}`, {
				bubbles: true,
				detail: { instance: this }
			})
		);
	}

	_bindUI() {
		this._onTriggerClick = this._onTriggerClick.bind(this);
		this._onCloserClick = this._onCloserClick.bind(this);
		this._onBeforeMatch = this._onBeforeMatch.bind(this);
		this._onEscapeKey = this._onEscapeKey.bind(this);
		this._onBlurCheck = this._onBlurCheck.bind(this);
		this._onOutsideClick = this._onOutsideClick.bind(this);

		this.elNode.addEventListener('click', this._onTriggerClick);
		this.elNode.addEventListener('click', this._onCloserClick);

		if (this.config.hiddenUntilFound) {
			this.elNode.addEventListener('beforematch', this._onBeforeMatch, true);
		}

		if (this.config.keyboardNavigation) {
			this._onItemKeydown = this._onItemKeydown.bind(this);
			this.elPanel.addEventListener('keydown', this._onItemKeydown);
		}
	}

	_unbindUI() {
		this.elNode.removeEventListener('click', this._onTriggerClick);
		this.elNode.removeEventListener('click', this._onCloserClick);

		if (this.config.hiddenUntilFound) {
			this.elNode.removeEventListener('beforematch', this._onBeforeMatch, true);
		}

		if (this.config.keyboardNavigation) {
			this.elPanel.removeEventListener('keydown', this._onItemKeydown);
		}
	}

	_bindGlobalListeners() {
		if (this.config.closeOnEscape) {
			document.addEventListener('keydown', this._onEscapeKey);
		}

		if (this.config.closeOnBlur) {
			document.addEventListener('focusin', this._onBlurCheck);
		}

		if (this.config.closeOnOutsideClick) {
			document.addEventListener('click', this._onOutsideClick);
		}
	}

	_unbindGlobalListeners() {
		document.removeEventListener('keydown', this._onEscapeKey);
		document.removeEventListener('focusin', this._onBlurCheck);
		document.removeEventListener('click', this._onOutsideClick);
	}

	_onMediaChange(event) {
		if (event.matches) {
			this._enable();
		} else {
			this._disable();
		}
	}

	_onTriggerClick(event) {
		const trigger = event.target.closest(`[data-js-${this.namespace}-button]`);

		if (!trigger || trigger !== this.elButton) return;

		this.toggle();
	}

	_onCloserClick(event) {
		const selector = `[data-js-${this.namespace}-close]`;
		const closer = event.target.closest(selector);

		if (!closer) return;

		this.close();
	}

	_onBeforeMatch(event) {
		const panel = event.target.closest(`[data-js-${this.namespace}-panel]`);

		if (!panel || panel !== this.elPanel) return;

		this.open();
	}

	_onEscapeKey(e) {
		if (e.key !== 'Escape') return;

		this.close(true);
	}

	/** Deferred: prevent focusin firing before activeElement updates. */
	_onBlurCheck() {
		setTimeout(() => {
			const focused = document.activeElement;

			if (!focused || focused === document.body) return;

			if (this.elNode.contains(focused)) return;

			this.close(false);
		}, 0);
	}

	_onOutsideClick(e) {
		if (this.elNode.contains(e.target)) return;

		this.close(false);
	}

	_onItemKeydown(event) {
		const itemSelector = `[data-js-${this.namespace}-item]`;

		if (!event.target.closest(itemSelector)) return;

		const items = this.elPanel.querySelectorAll(itemSelector);

		verticalKeyNavigationHandler({
			event,
			items,
			getTrigger: (el) => el,
			loop: this.config.loopNavigation
		});
	}
}

export default function factory(selector = DEFAULT_SELECTOR, settings = {}) {
	const listNode = document.querySelectorAll(selector);

	if (!listNode.length) return;

	return Array.from(listNode).map((node) => {
		const config = nodeConfig(node, NAMESPACE, settings, DEFAULT_SETTINGS);
		const module = new Disclosure(node, config);

		module.init();

		return module;
	});
}
